import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import {
  Alert,
  Link as MuiLink,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import {
  usePublicationCatalogs,
  usePublicationChannelBindings,
  usePublicationSalesChannels,
} from '../../hooks';
import { useToast } from '../../lib/useToast';
import { PUBLICATION_CHANNEL_KIND_LABELS } from '../../model/options';
import type { PublicationCatalog, PublicationSalesChannel } from '../../model/publicationTypes';
import { PublicationChannelBindingsDialog } from './PublicationChannelBindingsDialog';

interface ChannelRowProps {
  channel: PublicationSalesChannel;
  /** id → catalog lookup for rendering the persisted PRIMARY binding + overlay count. */
  catalogById: Map<string, PublicationCatalog>;
  onManageBindings: (channel: PublicationSalesChannel) => void;
}

/**
 * `canManage` is not needed here — the row's "Manage bindings" action is always shown (a viewer can
 * open the dialog to inspect persisted bindings read-only); `PublicationChannelBindingsDialog`
 * itself hides the set-primary/add-overlay controls when `!canManage`.
 */
function ChannelRow({ channel, catalogById, onManageBindings }: ChannelRowProps) {
  const { t } = useTranslation('catalog');
  // Persisted catalog bindings (primary + overlays) — per row; cached per channel id.
  const bindingsQuery = usePublicationChannelBindings(channel.id);
  const primaryCatalog = bindingsQuery.data?.primaryCatalogId
    ? catalogById.get(bindingsQuery.data.primaryCatalogId)
    : undefined;
  const overlayCount = bindingsQuery.data?.overlays.length ?? 0;

  return (
    <TableRow hover>
      <TableCell>
        <Typography fontWeight={600}>{channel.name}</Typography>
        <Typography variant="caption" color="text.secondary" component="code">
          {channel.code}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip size="small" variant="outlined" label={t(PUBLICATION_CHANNEL_KIND_LABELS[channel.channelKind])} />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          color={channel.active ? 'success' : 'default'}
          variant={channel.active ? 'filled' : 'outlined'}
          label={
            channel.active
              ? t('publicationChannelsPanel.table.activeYes')
              : t('publicationChannelsPanel.table.activeNo')
          }
        />
      </TableCell>
      <TableCell>
        {primaryCatalog ? (
          <Typography variant="body2">
            {primaryCatalog.name} ({primaryCatalog.code})
          </Typography>
        ) : (
          <Button size="small" variant="text" onClick={() => onManageBindings(channel)}>
            {t('publicationChannelsPanel.table.manageBindings')}
          </Button>
        )}
      </TableCell>
      <TableCell align="right">{overlayCount > 0 ? overlayCount : '-'}</TableCell>
      <TableCell align="right">
        <Tooltip title={t('publicationChannelsPanel.table.manageBindings')}>
          <IconButton
            size="small"
            aria-label={t('publicationChannelsPanel.table.manageBindingsAria', { name: channel.name })}
            onClick={() => onManageBindings(channel)}
          >
            <LinkRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

/**
 * Sales channels + catalog bindings tab of the publication workspace. Read-only channel list —
 * channel identity is created/managed in the unified registry (/settings/channels) and PROJECTED
 * into web-catalog-publication-service (the create flow here was removed by plan-cleanup-001,
 * 2026-08-25); per-row "Manage bindings" opens `PublicationChannelBindingsDialog` for the
 * primary/overlay catalogs.
 */
export function PublicationChannelsPanel({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('catalog');
  const channelsQuery = usePublicationSalesChannels();
  const catalogsQuery = usePublicationCatalogs();
  const { toast, success, error: notifyError, clear } = useToast();

  const [bindingsTarget, setBindingsTarget] = useState<PublicationSalesChannel | null>(null);

  if (channelsQuery.isLoading) {
    return <LoadingState label={t('publicationChannelsPanel.loading')} />;
  }
  if (channelsQuery.isError) {
    return (
      <ErrorState
        message={channelsQuery.error?.message}
        onRetry={() => void channelsQuery.refetch()}
      />
    );
  }

  const channels = channelsQuery.data ?? [];
  const catalogs = catalogsQuery.data ?? [];

  return (
    <Box>
      {/* plan-tenant-001 Phase 5: channel identity moved to the unified registry (/settings/channels). */}
      <Alert severity="info" sx={{ mb: 2 }} data-testid="publication-identity-moved-banner">
        {t('publicationChannelsPanel.identityMovedBanner')}{' '}
        <MuiLink component={RouterLink} to="/settings/channels">→</MuiLink>
      </Alert>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('publicationChannelsPanel.description')}
        </Typography>
      </Stack>

      {channels.length === 0 ? (
        <EmptyState
          title={t('publicationChannelsPanel.empty.title')}
          description={t('publicationChannelsPanel.empty.description')}
        />
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('publicationChannelsPanel.table.channel')}</TableCell>
                <TableCell>{t('publicationChannelsPanel.table.kind')}</TableCell>
                <TableCell>{t('publicationChannelsPanel.table.active')}</TableCell>
                <TableCell>{t('publicationChannelsPanel.table.primaryCatalog')}</TableCell>
                <TableCell align="right">{t('publicationChannelsPanel.table.overlayCount')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {channels.map((c) => (
                <ChannelRow
                  key={c.id}
                  channel={c}
                  catalogById={new Map(catalogs.map((cat) => [cat.id, cat]))}
                  onManageBindings={setBindingsTarget}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <PublicationChannelBindingsDialog
        open={bindingsTarget !== null}
        channel={bindingsTarget}
        catalogs={catalogs}
        canManage={canManage}
        onClose={() => setBindingsTarget(null)}
        onResult={(severity, message) => (severity === 'success' ? success(message) : notifyError(message))}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
}
